import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { take } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { IconService } from '../../services/icon.service';
import { MembershipsService } from '../../services/memberships.service';

/**
 * Sticky banner that surfaces when the logged-in user has a PENDING
 * membership awaiting payment confirmation. Renders nothing for users
 * without one (most users — the dashboard cost is one boolean fetch).
 */
@Component({
  selector: 'app-pending-membership-banner',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule],
  // Host's display is bound directly — `none` removes it from layout
  // entirely when there's no pending membership; `contents` makes it
  // transparent when visible so the inner div participates in the parent.
  host: {
    '[style.display]': 'visible() ? "contents" : "none"',
  },
  template: `
    @if (visible()) {
      <div class="pending-banner">
        <fa-icon [icon]="iconService.getIcon('clock')" class="pending-banner__icon" />
        <div class="pending-banner__copy">
          <strong>Your membership is pending activation.</strong>
          We're waiting for your payment to clear. This usually only takes a minute or two —
          your full member access unlocks automatically once it does.
        </div>
      </div>
    }
  `,
  styles: [`
    .pending-banner {
      display: flex;
      align-items: flex-start;
      gap: var(--ams-space-3);
      padding: var(--ams-space-3) var(--ams-space-5);
      background: var(--ams-color-warning-subtle);
      color: var(--ams-color-text-primary);
      border-bottom: 1px solid var(--ams-color-warning);
      font-size: var(--ams-font-size-sm);

      &__icon {
        color: var(--ams-color-warning);
        flex-shrink: 0;
        padding-top: 2px;
      }

      &__copy {
        line-height: 1.5;

        strong {
          margin-right: var(--ams-space-1);
        }
      }
    }
  `],
})
export class PendingMembershipBannerComponent implements OnInit {
  private readonly memberships = inject(MembershipsService);
  private readonly auth = inject(AuthService);
  readonly iconService = inject(IconService);

  readonly visible = signal(false);

  ngOnInit(): void {
    if (!this.auth.isAuthenticated()) return;
    this.memberships.getMyPendingMembership().pipe(take(1)).subscribe({
      next: (res) => this.visible.set(!!res?.hasPending),
      // Not critical — silent on errors so the banner just doesn't appear.
      error: () => {},
    });
  }
}
